import { datadogRum } from '@datadog/browser-rum';

interface ApiError {
  data?: {
    code?: string;
    error?: string;
  };
  code?: string;
}

/**
 * Helper to check if a parsed JSON value has a valid code field
 */
function hasValidCodeField(parsed: unknown): boolean {
  if (!parsed || typeof parsed !== 'object') {
    return false;
  }

  if (Array.isArray(parsed) && parsed.length > 0) {
    const firstItem = parsed[0];
    return (
      firstItem &&
      typeof firstItem === 'object' &&
      'code' in firstItem &&
      typeof firstItem.code === 'string'
    );
  }

  return 'code' in parsed && typeof (parsed as any).code === 'string';
}

/**
 * Type guard to check if an error matches the API error interface.
 * Validates that the error has either a data.code, data.error, or top-level code property.
 * Also handles stringified JSON in the data field.
 */
export function isApiError(error: unknown): error is ApiError {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const err = error as Record<string, unknown>;

  if ('data' in err && typeof err.data === 'object' && err.data !== null) {
    const data = err.data as Record<string, unknown>;
    const hasValidCode = 'code' in data && typeof data.code === 'string';
    const hasValidError = 'error' in data && typeof data.error === 'string';
    return hasValidCode || hasValidError;
  }

  if ('data' in err && typeof err.data === 'string') {
    try {
      const parsed = JSON.parse(err.data);
      return hasValidCodeField(parsed);
    } catch {}
  }

  return 'code' in err && typeof err.code === 'string';
}

/**
 * Helper to extract code from a parsed JSON value
 */
function extractCodeFromParsedJson(parsed: unknown): string | undefined {
  if (!parsed || typeof parsed !== 'object') {
    return undefined;
  }

  if (Array.isArray(parsed) && parsed.length > 0) {
    const firstItem = parsed[0];
    if (firstItem && typeof firstItem === 'object' && 'code' in firstItem) {
      const code = (firstItem as any).code;
      return typeof code === 'string' ? code : undefined;
    }
  }

  if ('code' in parsed) {
    const code = (parsed as any).code;
    return typeof code === 'string' ? code : undefined;
  }

  return undefined;
}

/**
 * Extracts error code from API error response.
 * Handles multiple formats:
 * - {data: {code: "..."}}
 * - {code: "..."}
 * - {data: '{"code":"..."}'} (stringified JSON)
 *
 * @param error - The error object from RTK Query or fetch call
 * @returns The error code string, or undefined if not found
 */
export function extractErrorCode(error: unknown): string | undefined {
  if (!isApiError(error)) {
    return undefined;
  }

  const candidate = error.data?.code || error.code;
  if (typeof candidate === 'string') {
    return candidate;
  }

  if (typeof error.data === 'string') {
    try {
      const parsed = JSON.parse(error.data);
      return extractCodeFromParsedJson(parsed);
    } catch {}
  }

  return undefined;
}
export interface MissingFieldError {
  field: string;
  displayName: string;
}

/**
 * Extracts error message from IQ API error response.
 *
 * IQ API returns errors in the format: `{data: {error: "message"}}`
 * This utility safely navigates the error structure and returns the message string.
 *
 * When RTK Query uses `responseHandler: 'text'`, error.data may be a JSON string.
 * This function handles both parsed objects and stringified JSON.
 *
 * @param error - The error object from RTK Query or fetch call
 * @returns The error message string, or undefined if the error doesn't match the expected format
 *
 */
export function extractIqApiErrorMessage(error: unknown): string | undefined {
  let errorData = (error as any)?.data;

  if (!errorData) {
    return undefined;
  }

  // Handle stringified JSON (from responseHandler: 'text')
  if (typeof errorData === 'string') {
    try {
      errorData = JSON.parse(errorData);
    } catch {
      // Not valid JSON, return undefined
      return undefined;
    }
  }

  const message = errorData?.error;
  return typeof message === 'string' ? message : undefined;
}

/**
 * Appointment booking error codes returned by the appointments API.
 *
 * These error codes are returned by the POST /calendar/appointments endpoint
 * when appointment booking fails due to availability issues. The codes appear
 * in the error response as either { data: { code: "ERROR_CODE" } } or { code: "ERROR_CODE" }.
 *
 * Backend service: equip-vesta-api (calendar/appointments controller)
 * Related ticket: PT-3250
 *
 * @see https://github.com/equiphealth/equip-vesta-api (appointments service)
 */
export const BOOKING_ERROR_CODES = {
  /** Returned when the selected time slot is no longer available for patient support */
  PATIENT_SUPPORT_NOT_AVAILABLE: 'PATIENT_SUPPORT_NOT_AVAILABLE_ERROR',
  /** Returned when the provider is no longer available for the selected time slot */
  PROVIDER_NOT_AVAILABLE: 'PROVIDER_NOT_AVAILABLE_ERROR',
  /** Returned when a batch request contains more than one distinct host */
  APPOINTMENT_BATCH_MULTIPLE_HOSTS: 'APPOINTMENT_BATCH_MULTIPLE_HOSTS',
} as const;

/**
 * Maps booking error codes to user-friendly error messages
 *
 * @param errorCode - The error code from the appointment API
 * @returns User-friendly error message
 */
export function getBookingErrorMessage(errorCode: string | undefined): string {
  const DEFAULT_MESSAGE =
    "We weren't able to assign this provider or schedule their appointments. No changes were made — please try again.";

  if (!errorCode) {
    return DEFAULT_MESSAGE;
  }

  switch (errorCode) {
    case BOOKING_ERROR_CODES.PATIENT_SUPPORT_NOT_AVAILABLE:
      return 'The patient or support attendee is no longer available for this time slot. Another user may have just booked it. Please select a different time.';
    case BOOKING_ERROR_CODES.PROVIDER_NOT_AVAILABLE:
      return 'This provider is no longer available at this time. Please select a different time slot.';
    case BOOKING_ERROR_CODES.APPOINTMENT_BATCH_MULTIPLE_HOSTS:
      return 'All appointments in a batch must be with the same provider.';
    default:
      return DEFAULT_MESSAGE;
  }
}

/**
 * Parses missing field errors from IQ API error response.
 *
 * IQ API returns missing field errors in format: `{data: {error: "message", missingFields: ["field1", "field2"]}}`
 * This utility extracts the missing fields and maps them to user-friendly display names.
 *
 * When RTK Query uses `responseHandler: 'text'`, error.data may be a JSON string.
 * This function handles both parsed objects and stringified JSON.
 *
 * If no structured missing fields are found but the error message indicates a specific
 * missing field (e.g., "Equip App account"), a synthetic field error is returned as fallback.
 *
 * @param error - The error object from RTK Query or fetch call
 * @returns Array of missing field errors with internal field names and display names
 *
 */
export function parseMissingFieldErrors(error: unknown): MissingFieldError[] {
  let errorData = (error as any)?.data;

  if (!errorData) {
    return [];
  }

  // Handle stringified JSON (from responseHandler: 'text')
  if (typeof errorData === 'string') {
    try {
      errorData = JSON.parse(errorData);
    } catch {
      return [];
    }
  }

  const fieldDisplayMap: Record<string, string> = {
    dateOfBirth: 'Date of Birth',
    dob: 'Date of Birth',
    timezone: 'Timezone',
    timeZone: 'Timezone',
    admitDate: 'Admit Date',
    targetAdmissionDate: 'Admit Date',
    equipAppAccount: 'Equip App Account (must be activated)',
    appAccount: 'Equip App Account (must be activated)',
  };

  const missingFieldsMap = new Map<string, MissingFieldError>();

  if (Array.isArray(errorData.missingFields)) {
    errorData.missingFields.forEach((field: string) => {
      const displayName = fieldDisplayMap[field] || field;
      missingFieldsMap.set(displayName, {
        field,
        displayName,
      });
    });
  }

  // TODO: Verify with IQ team whether 'missing' key is intentional or should be consolidated with 'missingFields'
  if (Array.isArray(errorData.missing)) {
    errorData.missing.forEach((field: string) => {
      const displayName = fieldDisplayMap[field] || field;
      missingFieldsMap.set(displayName, {
        field,
        displayName,
      });
    });
  }

  // If no structured fields found, try fallback detection from error message
  // Check both 'error' and 'message' fields as different APIs use different keys
  const errorMessageText = errorData.error || errorData.message;

  if (missingFieldsMap.size === 0 && typeof errorMessageText === 'string') {
    const errorMessage = errorMessageText.toLowerCase();

    // Detect missing Equip App Account from error message
    // Matches: "equip app account", "patient account", "account inactive", "account not found", etc.
    const accountKeywords = [
      'equip app account',
      'patient account',
      'account inactive',
      'account not found',
      'no account',
      'account is inactive',
      'inactive account',
    ];

    const accountRelated = accountKeywords.some((keyword) =>
      errorMessage.includes(keyword),
    );

    if (accountRelated) {
      missingFieldsMap.set('Equip App Account (must be activated)', {
        field: 'equipAppAccount',
        displayName: 'Equip App Account (must be activated)',
      });

      // Log fallback detection for monitoring
      datadogRum.addAction('missing_field_fallback_detected', {
        detectedField: 'equipAppAccount',
        errorMessage: errorMessageText,
        matchedPattern: accountKeywords.find((k) => errorMessage.includes(k)),
        originalDataType: typeof (error as any)?.data,
      });
    } else {
      // Log when we have error data but no fields detected (for debugging new scenarios)
      datadogRum.addAction('missing_field_parse_no_match', {
        errorMessagePreview: errorMessageText?.slice(0, 100),
        hasError: !!errorData.error,
        hasMessage: !!errorData.message,
        hasMissingFields: Array.isArray(errorData.missingFields),
        hasMissing: Array.isArray(errorData.missing),
        originalDataType: typeof (error as any)?.data,
      });
    }
  }

  const result = Array.from(missingFieldsMap.values());

  return result;
}
